import React, { useEffect, useRef, useState } from "react";
import LandscapeProgramLayout from "../LandscapeProgramLayout";
import BottomNav from "../BottomNav";
import cn from "../../../utils/cn";
import { Pause, Triangle } from "../../Icons";
import useCountDown from "../../../hooks/useCountDown";
import { useAuthContext } from "../../../context/auth";
import animation from "../../animation.json";
// @ts-ignore
import styles from "./WorkoutType2.module.css";

import Lottie from "react-lottie";
import useAudio from "../../../hooks/useAudio";

const ROUND_COLOR = ["#FD8463", "#F2D759", "#47D5D1"];
function Round({
  number,
  active,
  callback,
  complete,
  handleShowNext,
  round,
  syncStartTime,
}: {
  number: number;
  active: boolean;
  complete: boolean;
  callback: () => void;
  handleShowNext: () => void;
  round: number;
  syncStartTime: number;
}) {
  const SET_TIME = round === 1 ? 60 : round === 2 ? 40 : 20;
  const REST_TIME = round === 1 ? 30 : round === 2 ? 20 : 20;
  const { did } = useAuthContext();
  const [repeatCount, setRepeatCount] = useState(() =>
    number === 1 && round === 1 ? 1 : 0
  );
  const [count, setCount] = useState(() =>
    number === 1 && round === 1 ? SET_TIME : REST_TIME
  );
  const play = useAudio("countdown.mp3");
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const localStartTimeRef = useRef<number | null>(null);
  const isCompletedRef = useRef(false);

  useEffect(() => {
    if (active && !isCompletedRef.current) {
      // Set local start time when this Round becomes active
      if (!localStartTimeRef.current) {
        localStartTimeRef.current = syncStartTime;
      }

      const startTime = localStartTimeRef.current;
      const totalDuration = SET_TIME + REST_TIME;

      const tick = () => {
        const currentTime = Date.now();
        const elapsed = currentTime - startTime;

        // If startTime is in the future, show initial values
        if (elapsed < 0) {
          setCount(number === 1 && round === 1 ? SET_TIME : REST_TIME);
          timerRef.current = setTimeout(tick, Math.abs(elapsed));
          return;
        }

        // Calculate total seconds passed
        const totalSecondsPassed = Math.floor(elapsed / 1000);

        // Determine which phase we're in
        let currentRepeatCount: number;
        let currentCount: number;

        if (totalSecondsPassed < SET_TIME) {
          // First SET phase
          currentRepeatCount = number === 1 && round === 1 ? 1 : 0;
          currentCount = SET_TIME - totalSecondsPassed;
        } else if (totalSecondsPassed < totalDuration) {
          // REST phase
          currentRepeatCount = 1;
          currentCount = totalDuration - totalSecondsPassed;
        } else if (totalSecondsPassed < totalDuration + SET_TIME) {
          // Second SET phase
          currentRepeatCount = 1;
          currentCount = totalDuration + SET_TIME - totalSecondsPassed;
        } else {
          // Completed
          callback();
          isCompletedRef.current = true;
          return;
        }

        setRepeatCount((prev) => {
          if (currentRepeatCount !== prev) {
            return currentRepeatCount;
          }
          return prev;
        });

        setCount((prev) => {
          if (currentCount !== prev) {
            if (currentCount === 4) {
              did === "1" && play();
            }
          }
          return currentCount;
        });

        // Calculate next tick to align with second boundaries
        const nextExpectedTime = startTime + (totalSecondsPassed + 1) * 1000;
        const delay = nextExpectedTime - Date.now();

        if (!isCompletedRef.current) {
          timerRef.current = setTimeout(tick, Math.max(0, delay));
        }
      };

      // Start immediately to show initial count
      tick();

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    } else if (!active) {
      // Reset when inactive
      localStartTimeRef.current = null;
      isCompletedRef.current = false;
      setRepeatCount(number === 1 && round === 1 ? 1 : 0);
      setCount(number === 1 && round === 1 ? SET_TIME : REST_TIME);
    }
  }, [active, number, round, SET_TIME, REST_TIME, callback, play, did, syncStartTime]);

  if (complete) {
    return <img src={`/logos/${number}.png`} alt="workout" />;
  }
  return (
    <div
      className={cn(
        "flex items-center justify-center flex-col gap-[10px]",
        "border-b border-border",
        (number === 2 || number === 5) && "border-l border-r border-border",
        repeatCount % 2 === 0 && repeatCount > 1 && styles.blinking
      )}
      style={{
        backgroundColor: active ? ROUND_COLOR[round - 1] : "transparent",
      }}
    >
      <h1
        className={cn(
          "text-[120px] font-bold text-white opacity-50",
          active && "text-black opacity-100"
        )}
      >
        {repeatCount % 2 === 0 && active ? "휴식" : `${number} SET`}
      </h1>
      {active && (
        <>
          <p className="text-[100px] text-white">{count}</p>
        </>
      )}
    </div>
  );
}

function WorkoutType3({ round, second }: { round: number; second?: boolean }) {
  const [step, setStep] = useState(0);
  const { sendSetStep, step: totalStep, did, emitter } = useAuthContext();
  const [showNext, setShowNext] = useState(false);
  const [syncTime, setSyncTime] = useState(Date.now());
  const [isPaused, setIsPaused] = useState(false);

  const handleShowNext = () => {
    setShowNext(true);
    setTimeout(() => {
      setShowNext(false);
    }, 10000);
  };

  useEffect(() => {
    setSyncTime(Date.now());
    setIsPaused(false);

    emitter.current.on("resume", () => {
      setSyncTime(Date.now());
      setIsPaused(false);
    });
    emitter.current.on("pause", () => {
      setIsPaused(true);
    });
    return () => {
      emitter.current.off("resume");
      emitter.current.off("pause");
    };
  }, []);

  return (
    <LandscapeProgramLayout step={4}>
      <div className={cn("bg-bg w-full relative h-[883px]")}>
        {showNext && (
          <div className="absolute w-full h-full pt-[180px] flex flex-col items-center bg-bg z-50 gap-[80px]">
            <h1 className="text-[100px] font-extrabold leading-[120px] gradient-text uppercase">
              next station
            </h1>
            <Lottie
              options={{
                loop: true,
                animationData: animation,
                rendererSettings: {
                  preserveAspectRatio: "xMidYMid slice",
                },
              }}
              width={710}
              height={330}
              isClickToPauseDisabled
            />
          </div>
        )}
        <img
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-10 w-[1389px] h-[682px] z-10"
          src="/logo.png"
          alt="workout"
        />
        <div className="grid grid-cols-3 grid-rows-2 z-20 relative h-full">
          {Array(6)
            .fill(0)
            .map((_, i) => (
              <Round
                active={i === step && !isPaused}
                complete={i < step}
                number={i + 1}
                round={round}
                syncStartTime={syncTime}
                handleShowNext={handleShowNext}
                callback={() => {
                  if (i === step) {
                    setStep((prev) => {
                      if (prev === 5) {
                        if (round === 3) {
                          setTimeout(() => {
                            sendSetStep(totalStep + 1);
                          }, 3000);
                        } else {
                          sendSetStep(totalStep + 1);
                        }
                        return prev + 1;
                      }
                      if (i === prev) {
                        handleShowNext();
                        return prev + 1;
                      }
                      return prev;
                    });
                  }
                }}
              />
            ))}
        </div>
      </div>
      <BottomNav />
    </LandscapeProgramLayout>
  );
}

export default WorkoutType3;
